'use client';

import * as React from 'react';
import cx from 'classnames';

import { Icon, IconNames } from '@components/icon';
import { Typography } from '@components/typography';

import { SavvyPromiseModal } from './savvy-promise-modal';
import { SAVVY_PROMISE_BADGE_LABEL } from './savvy-promise-copy';

export type SavvyPromiseBadgeVariant = 'inline' | 'belowCard';

export interface SavvyPromiseBadgeProps {
  /**
   * Visual treatment.
   * - `inline`: chip-sized row used directly under booking buttons /
   *   inside an off-platform CTA cluster.
   * - `belowCard`: same chip but centered with extra top margin, used
   *   directly under the on-platform listing pricing card (Option A2).
   */
  variant?: SavvyPromiseBadgeVariant;
  /** Optional `data-id` override for analytics; defaults are variant-scoped. */
  analyticsRef?: string;
  /** Extra Tailwind classes for the wrapper. */
  className?: string;
}

const DEFAULT_ANALYTICS_REF: Record<SavvyPromiseBadgeVariant, string> = {
  inline: 'savvy_promise_badge_inline',
  belowCard: 'savvy_promise_badge_below_card',
};

/**
 * "The Savvy Promise, Book with Confidence" badge. Renders as a small
 * mint-green shield tile, the badge label, and a trailing info-circle
 * indicator. Clicking opens the SavvyPromiseModal.
 */
export const SavvyPromiseBadge: React.FC<SavvyPromiseBadgeProps> = ({
  variant = 'inline',
  analyticsRef,
  className,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const openModal = React.useCallback(() => setIsOpen(true), []);
  const closeModal = React.useCallback(() => setIsOpen(false), []);

  const dataId = analyticsRef ?? DEFAULT_ANALYTICS_REF[variant];

  const wrapperClasses = cx(
    {
      'flex w-full justify-center': variant === 'belowCard',
      'inline-flex': variant !== 'belowCard',
    },
    className
  );

  return (
    <>
      <div className={wrapperClasses}>
        <button
          type="button"
          onClick={openModal}
          data-id={dataId}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          className="group inline-flex items-center gap-2 rounded-md py-1 px-1.5 transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-dark hover:bg-pool/30"
        >
          <span
            className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-pool/60 text-primary-main"
            aria-hidden="true"
          >
            <Icon name={IconNames.SHIELD_USER} size="xs" />
          </span>
          <Typography
            component="span"
            variant="body2"
            className="font-medium text-primary-main"
          >
            {SAVVY_PROMISE_BADGE_LABEL}
          </Typography>
          <Icon
            name={IconNames.INFO_CIRCLE}
            size="xs"
            className="text-primary-main/60 group-hover:text-primary-main"
          />
        </button>
      </div>
      <SavvyPromiseModal isOpen={isOpen} onClose={closeModal} />
    </>
  );
};

SavvyPromiseBadge.displayName = 'SavvyPromiseBadge';
